/* ============================================================
   COIN FAIRY — PER-PRODUCT ADD-ONS
   assets/coin-fairy-addons.js
   ============================================================

   INSTALL
   1. Online Store > Themes > Edit code > Assets > Add a new asset
      > Create blank file > coin-fairy-addons.js > paste this in.
   2. In layout/theme.liquid, just before </body>:
        <script src="{{ 'coin-fairy-addons.js' | asset_url }}" defer></script>
   3. In each PageFly product page, add a Custom HTML element
      containing only:  <div id="cf-addons"></div>

   HOW IT WORKS
   PageFly's Add to Cart is type="button" and posts the ENTIRE
   product form as FormData to /cart/add.js. (Verified live on
   thecoinfairy.com, Aug 2026.) So this script injects hidden
   items[] inputs into that form just before it's serialized, and
   PageFly carries them to the cart for us. The native button is
   never replaced or hidden.

   Shopify's cart endpoint accepts nested array notation:
       items[0][id]                  -> the main product
       items[1][id]                  -> an add-on
       items[1][properties][Label]   -> that add-on's name

   SAFETY
   Unavailable variants are filtered out at render time. This
   matters: one out-of-stock variant inside an items[] batch
   returns 422 and kills the ENTIRE add-to-cart, main product
   included. Never show an add-on you can't actually sell.
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     CONFIG — the only part you edit
     ============================================================ */

  // Which add-ons appear on which product page.
  // Key = PRODUCT id. Values = slugs from ADDON_CATALOG below.
  //
  // The Learning Game already ships with 2 pouches AND a tooth tracker,
  // so neither belongs here as an add-on — selling someone a second copy
  // of what's in the box they're buying reads as a trick. Those two are
  // refill items: they belong on each other's pages and in the shop, not
  // stapled to the core product.
  var ADDONS_BY_PRODUCT = {
    7439941435497: ['wing-on-a-string'],                   // STEM Learning Game
    7781857853545: ['wing-on-a-string'],                   // Savings Bank
    9420287311977: ['tooth-tracker', 'wing-on-a-string'],  // 2 Tooth Pouches
    9420291244137: ['tooth-pouches', 'wing-on-a-string'],  // Tooth Tracker
    7785591865449: ['tooth-pouches', 'tooth-tracker']      // Wing on a String
  };

  // Every add-on this store can offer. VARIANT ids, live-verified Aug 2026.
  var ADDON_CATALOG = {

    'wing-on-a-string': {
      title: 'Wing on a String',
      price: '$12.99',
      description: 'Glimmer suctions to the bathroom mirror and reports brushing back to the Coin Fairy.',
      variants: { plain: 43215800369257 },
      personalizes: null,
      // Optional. Omit and the script uses the product's first Shopify image.
      // Set here when that image is wrong — this product's only two photos
      // are both "coming soon" placeholders from its Coin Fairy Stuffy days.
      image: null
    },

    'tooth-tracker': {
      title: 'Tooth Tracker with Stickers',
      price: '$4.99',
      description: 'A replacement board and fresh sticker sheet. One is already included in the Learning Game.',
      variants: { plain: 47934109515881 },
      personalizes: null
    },

    'tooth-pouches': {
      title: '2 Tooth Pouches',
      price: '$6.99',
      description: 'A spare pair for a second child, or a fresh set when the first two are well loved. Two are already included in the Learning Game.',
      variants: {
        plain: 47934105256041,
        // TODO: create the No/Yes Personalization option on this product,
        // then put the "Yes" variant id here. Until then the personalization
        // field stays hidden and the pouches add as a plain line.
        personalized: null
      },
      personalizes: { label: 'Name for Engraving', maxlength: 12, fields: 2 }
    },

    'savings-bank': {
      title: 'Coin Fairy Savings Bank',
      price: '$40.00',
      description: 'Bamboo and heavy-duty acrylic, handcrafted in the USA.',
      variants: {
        plain: 47749779685481,        // "No"  $40.00
        personalized: 47749779652713  // "Yes" $45.00
      },
      personalizes: { label: 'Name for Engraving', maxlength: 12, fields: 1 }
    }
  };

  var COPY = {
    heading: 'Add to your order',
    personalizeToggle: 'Personalize this',
    feeNotice: 'One $5 personalization fee per order, no matter how many items you personalize — adjusted at checkout.'
  };

  /* ============================================================
     Nothing below here needs editing
     ============================================================ */

  var mount, form, productId, rendered = [];

  function findForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="id"]')) return forms[i];
    }
    return forms[0] || null;
  }

  function currentProductId() {
    // PageFly writes this hidden input into the product form.
    var el = form && form.querySelector('[name="product-id"]');
    if (el && el.value) return String(el.value);
    var box = document.querySelector('[data-pf-type="ProductBox"][data-product-id]');
    if (box) return String(box.getAttribute('data-product-id'));
    if (window.__pageflyProducts) {
      var keys = Object.keys(window.__pageflyProducts);
      if (keys.length === 1) return keys[0];
    }
    return null;
  }

  // One call to products.json gives us both what's sellable and what to
  // show. Keyed by variant id so the catalog stays the single source of
  // truth for which variant a card represents.
  function loadCatalog() {
    return fetch('/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var available = {}, images = {};
        (data.products || []).forEach(function (p) {
          // Variant-level featured_image is null across this store, so fall
          // back to the product's first image.
          var img = (p.images && p.images[0] && p.images[0].src) || null;
          (p.variants || []).forEach(function (v) {
            available[v.id] = v.available;
            images[v.id] = (v.featured_image && v.featured_image.src) || img;
          });
        });
        return { available: available, images: images };
      })
      .catch(function () { return { available: null, images: {} }; });
  }

  // Shopify CDN honours a width param; ask for exactly what we render
  // rather than shipping a 2000px product shot into a 64px box.
  function sized(url, w) {
    if (!url) return null;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- render ---------- */

  function render(catalog) {
    var availability = catalog.available;
    var images = catalog.images || {};
    var slugs = ADDONS_BY_PRODUCT[productId] || ADDONS_BY_PRODUCT[Number(productId)] || [];
    if (!slugs.length) return;

    var cards = '';
    slugs.forEach(function (slug) {
      var a = ADDON_CATALOG[slug];
      if (!a) return console.warn('[coin-fairy] unknown add-on slug:', slug);

      // Can we personalize? Only if a "Yes" variant exists AND is sellable.
      var canPersonalize = !!(a.personalizes && a.variants.personalized);
      if (canPersonalize && availability && availability[a.variants.personalized] === false) {
        canPersonalize = false;
      }

      // Is the plain variant sellable? If not, skip the card entirely —
      // showing it would 422 the whole batch.
      if (availability && availability[a.variants.plain] === false) {
        if (!canPersonalize) {
          console.warn('[coin-fairy] hiding "' + a.title + '" — no sellable variant.');
          return;
        }
      }

      rendered.push(slug);

      var fields = '';
      if (canPersonalize) {
        for (var f = 0; f < (a.personalizes.fields || 1); f++) {
          var id = 'cf-' + slug + '-name-' + f;
          var label = (a.personalizes.fields > 1)
            ? a.personalizes.label + ' ' + (f + 1)
            : a.personalizes.label;
          fields +=
            '<div class="cf-field">' +
              '<label class="cf-field-label" for="' + id + '">' + esc(label) +
                ' <span class="cf-max">(' + a.personalizes.maxlength + ' character maximum)</span></label>' +
              '<input type="text" class="cf-input cf-name" id="' + id + '" ' +
                'data-label="' + esc(label) + '" maxlength="' + a.personalizes.maxlength + '" ' +
                'placeholder="Add first name here..." autocomplete="off" autocapitalize="words">' +
            '</div>';
        }
      }

      // Prefer an explicit image from the catalog, else whatever Shopify
      // has. Products with no photo at all (the Tooth Tracker today) get
      // no <img> rather than a broken-image icon.
      var imgSrc = a.image || images[a.variants.plain] || null;
      var thumb = imgSrc
        ? '<img class="cf-thumb" src="' + esc(sized(imgSrc, 160)) + '" alt="" loading="lazy" width="64" height="64">'
        : '';

      cards +=
        '<div class="cf-addon" data-slug="' + esc(slug) + '" data-checked="false" data-personalized="false">' +
          '<label class="cf-addon-row">' +
            '<input type="checkbox" class="cf-check">' +
            thumb +
            '<span class="cf-addon-body">' +
              '<span class="cf-addon-title">' +
                '<span>' + esc(a.title) + '</span>' +
                '<span class="cf-addon-price">' + esc(a.price) + '</span>' +
              '</span>' +
              '<span class="cf-addon-desc">' + esc(a.description) + '</span>' +
            '</span>' +
          '</label>' +
          (canPersonalize
            ? '<div class="cf-personalize" hidden>' +
                '<label class="cf-toggle">' +
                  '<input type="checkbox" class="cf-personalize-check">' +
                  '<span>' + esc(COPY.personalizeToggle) + ' <em>+$5</em></span>' +
                '</label>' +
                '<div class="cf-fields" hidden>' + fields + '</div>' +
              '</div>'
            : '') +
        '</div>';
    });

    if (!cards) return;

    mount.innerHTML =
      '<fieldset class="cf-group">' +
        '<legend class="cf-legend">' + esc(COPY.heading) + '</legend>' +
        cards +
        '<p class="cf-fee" hidden>' + esc(COPY.feeNotice) + '</p>' +
      '</fieldset>';

    mount.classList.add('cf-addons');
    bind();
  }

  /* ---------- interactions ---------- */

  function bind() {
    mount.addEventListener('change', function (e) {
      var card = e.target.closest('.cf-addon');
      if (!card) return;

      if (e.target.classList.contains('cf-check')) {
        card.dataset.checked = e.target.checked ? 'true' : 'false';
        var p = card.querySelector('.cf-personalize');
        if (p) p.hidden = !e.target.checked;
        if (!e.target.checked) {
          var pc = card.querySelector('.cf-personalize-check');
          if (pc) { pc.checked = false; card.dataset.personalized = 'false'; }
          var ff = card.querySelector('.cf-fields');
          if (ff) ff.hidden = true;
        }
      }

      if (e.target.classList.contains('cf-personalize-check')) {
        card.dataset.personalized = e.target.checked ? 'true' : 'false';
        var f = card.querySelector('.cf-fields');
        if (f) {
          f.hidden = !e.target.checked;
          if (e.target.checked) {
            var known = firstName();
            f.querySelectorAll('.cf-name').forEach(function (i) { if (!i.value) i.value = known; });
            var first = f.querySelector('input');
            if (first) first.focus();
          }
        }
      }
      updateFee();
    });

    mount.addEventListener('input', function (e) {
      if (e.target.classList.contains('cf-name')) updateFee();
    });
  }

  function firstName() {
    // Reuse a name already typed anywhere on the page, including the
    // main product's own personalization field.
    var main = form && form.querySelector('[name^="properties["]');
    if (main && main.value.trim()) return main.value.trim();
    var found = '';
    mount.querySelectorAll('.cf-name').forEach(function (i) {
      if (!found && i.value.trim()) found = i.value.trim();
    });
    return found;
  }

  function activeCards() {
    return [].slice.call(mount.querySelectorAll('.cf-addon'))
      .filter(function (c) { return c.dataset.checked === 'true'; });
  }

  function updateFee() {
    var notice = mount.querySelector('.cf-fee');
    if (!notice) return;
    var any = activeCards().some(function (c) { return c.dataset.personalized === 'true'; });
    // The main product counts too if it has a name filled in.
    var main = form && form.querySelector('[name^="properties["]');
    if (main && main.value.trim()) any = true;
    notice.hidden = !any;
  }

  /* ---------- payload rewriting ----------

     WHY NOT INJECT HIDDEN INPUTS INTO THE FORM

     The obvious approach — rename the form's `id` to `items[0][id]`
     and append `items[1][...]` inputs — DOES NOT WORK with PageFly.
     Its _pf_cartAddFromForm serializes the form and then appends its
     OWN `id` field from internal state. The payload ends up with both
     a flat `id` and an `items[]` array, Shopify honors both, and the
     main product lands in the cart TWICE. (Diagnosed live, Aug 2026.)

     So instead we leave the DOM completely alone and rewrite the
     request body on its way out. One request, no duplicates, and
     nothing to restore afterwards.
     ------------------------------------------------------------- */

  function selectedAddons() {
    return activeCards().map(function (card) {
      var a = ADDON_CATALOG[card.dataset.slug];
      var personalized = card.dataset.personalized === 'true' && !!a.variants.personalized;
      var props = {};
      if (personalized) {
        card.querySelectorAll('.cf-name').forEach(function (input) {
          var v = input.value.trim();
          if (v) props[input.dataset.label] = v;
        });
      }
      // Underscore keys are hidden from the customer but show on the order.
      props._addon_of = productId;
      return {
        variantId: personalized ? a.variants.personalized : a.variants.plain,
        properties: props
      };
    });
  }

  function rewrite(fd) {
    var addons = selectedAddons();
    if (!addons.length) return fd;   // nothing ticked: send it untouched

    var out = new FormData();
    var mainId = null, mainQty = null, props = [];

    // FormData legitimately contains `id` twice here — once from the
    // form's hidden input, once appended by PageFly. Last one wins,
    // since that's the one reflecting the selected variant.
    fd.forEach(function (value, key) {
      if (key === 'id') { mainId = value; }
      else if (key === 'quantity') { mainQty = value; }
      else if (key.indexOf('properties[') === 0) { props.push([key.slice('properties'.length), value]); }
      else if (key.indexOf('items[') === 0) { /* drop anything stale */ }
      else { out.append(key, value); }
    });

    if (!mainId) return fd;          // can't identify the main product; bail safely

    out.append('items[0][id]', mainId);
    out.append('items[0][quantity]', mainQty || '1');
    props.forEach(function (p) { out.append('items[0][properties]' + p[0], p[1]); });

    addons.forEach(function (a, i) {
      var slot = i + 1;
      out.append('items[' + slot + '][id]', a.variantId);
      out.append('items[' + slot + '][quantity]', '1');
      Object.keys(a.properties).forEach(function (k) {
        out.append('items[' + slot + '][properties][' + k + ']', a.properties[k]);
      });
    });

    return out;
  }

  function patchNetwork() {
    if (window.__cfPatched) return;
    window.__cfPatched = true;

    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (url && url.indexOf('/cart/add') !== -1 && init && init.body instanceof FormData) {
          init.body = rewrite(init.body);
        }
      } catch (err) {
        console.error('[coin-fairy] payload rewrite failed, sending original:', err);
      }
      return origFetch.apply(this, arguments);
    };

    // Belt and braces: some themes still add via XHR.
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) {
      this.__cfUrl = u;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__cfUrl && String(this.__cfUrl).indexOf('/cart/add') !== -1 && body instanceof FormData) {
          arguments[0] = rewrite(body);
        }
      } catch (err) {
        console.error('[coin-fairy] payload rewrite failed (xhr):', err);
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ---------- styles ---------- */

  var CSS = [
    '.cf-addons{--cf-navy:#3D3580;--cf-gold:#A0854E;--cf-ink:#2B2A35;--cf-muted:#6E6B7B;--cf-line:#E2DFE8;--cf-soft:#F7F5FA;',
    'font-family:inherit;color:var(--cf-ink);max-width:34rem;margin:1.25rem 0}',
    '.cf-addons .cf-group{border:none;padding:0;margin:0}',
    '.cf-addons .cf-legend{font-size:1.4rem;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--cf-gold);padding:0;margin-bottom:.6rem}',
    '.cf-addons .cf-addon{border:1px solid var(--cf-line);border-radius:8px;padding:.9rem 1rem;margin-bottom:.65rem;transition:border-color .15s,background .15s}',
    '.cf-addons .cf-addon[data-checked="true"]{border-color:var(--cf-navy);background:var(--cf-soft)}',
    '.cf-addons .cf-addon-row{display:flex;align-items:flex-start;gap:.7rem;cursor:pointer;margin:0}',
    '.cf-addons .cf-check,.cf-addons .cf-personalize-check{width:1.15rem;height:1.15rem;margin:.1rem 0 0;accent-color:var(--cf-navy);flex:none;cursor:pointer}',
    '.cf-addons .cf-addon-body{flex:1 1 auto;min-width:0}',
    '.cf-addons .cf-thumb{width:64px;height:64px;flex:none;object-fit:cover;border-radius:6px;background:var(--cf-soft);border:1px solid var(--cf-line)}',
    '.cf-addons .cf-addon-title{display:flex;justify-content:space-between;gap:1rem;font-weight:600;font-size:1.2rem;line-height:1.35}',
    '.cf-addons .cf-addon-price{color:var(--cf-gold);white-space:nowrap}',
    '.cf-addons .cf-addon-desc{display:block;font-size:1.1rem;color:black;line-height:1.5;margin-top:.25rem}',
    '.cf-addons .cf-personalize{margin-top:.8rem;padding-top:.8rem;border-top:1px dashed var(--cf-line)}',
    '.cf-addons .cf-personalize[hidden],.cf-addons .cf-fields[hidden],.cf-addons .cf-fee[hidden]{display:none}',
    '.cf-addons .cf-toggle{display:flex;align-items:center;gap:.6rem;cursor:pointer;font-size:.88rem;font-weight:500;margin:0}',
    '.cf-addons .cf-toggle em{font-style:normal;color:var(--cf-gold);font-weight:600}',
    '.cf-addons .cf-fields{display:grid;gap:.6rem;margin-top:.7rem}',
    '.cf-addons .cf-field-label{display:block;font-size:1rem;font-weight:500;color:black);margin-bottom:.3rem}',
    '.cf-addons .cf-max{font-weight:400;opacity:.75}',
    '.cf-addons .cf-input{width:100%;padding:.7rem .85rem;border:1px solid var(--cf-line);border-radius:6px;font-size:1rem;font-family:inherit;color:var(--cf-ink);background:#fff;box-sizing:border-box}',
    '.cf-addons .cf-input:focus{outline:none;border-color:var(--cf-navy);box-shadow:0 0 0 3px rgba(61,53,128,.12)}',
    '.cf-addons .cf-fee{font-size:1.2rem;color:var(--cf-muted);line-height:1.5;margin:.9rem 0 0;padding:.6rem .8rem;background:var(--cf-soft);border-left:3px solid var(--cf-gold);border-radius:0 4px 4px 0}',
    '@media(prefers-reduced-motion:reduce){.cf-addons *{transition:none!important}}'
  ].join('');

  /* ---------- boot ---------- */

  function init() {
    mount = document.getElementById('cf-addons');
    if (!mount) return;
    form = findForm();
    if (!form) return console.warn('[coin-fairy] no /cart/add form found.');
    productId = currentProductId();
    if (!productId) return console.warn('[coin-fairy] could not determine product id.');

    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    patchNetwork();
    loadCatalog().then(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();